'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Flame, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'

interface HotEntry {
	code: string
	name: string
	count: number
}

interface HotStateResponse {
	trending: HotEntry | null
	allTime: HotEntry | null
}

/**
 * Two small pills showing the **most-routed destination states**, from
 * /api/hot-state (Redis-cached, read from Plausible's `Calculated` event):
 *
 *   - **Trending** — top destination over the last 30 days. Accent-colored and
 *     glow-pulsing (the pulse is auto-disabled under `prefers-reduced-motion`
 *     by the global rule in globals.css).
 *   - **All-time** — top destination of all time. A quieter "champion" badge;
 *     hidden when it's the same state as trending (would be redundant).
 *
 * Renders nothing until at least one qualifying state exists, so it never shows
 * an empty frame. Sits with the Recent routes in the empty state.
 *
 * The chip is fetched async, so to avoid the layout-shift "snap" of injecting
 * ~40px under the content below once /api/hot-state resolves, the block eases in
 * via Framer Motion — height (0 → auto) + opacity + a slight upward slide — so
 * neighbors settle smoothly rather than jump. The transition is ~220ms, well
 * within the app's <600ms animation budget. Under `prefers-reduced-motion` the
 * animation is collapsed to an instant cut via `useReducedMotion`, mirroring the
 * global reduced-motion rule in globals.css that already disables `hot-pulse`.
 *
 * `AnimatePresence` still renders nothing while there's no qualifying state, so
 * the "no empty frame" intent above is preserved (no placeholder height).
 */
export function HotStateChip() {
	const [data, setData] = useState<HotStateResponse | null>(null)
	// Collapse the entrance to an instant cut for users who opt out of motion.
	const prefersReducedMotion = useReducedMotion()

	useEffect(() => {
		let active = true
		fetch('/api/hot-state')
			.then((r) => (r.ok ? r.json() : null))
			.then((d: HotStateResponse | null) => {
				if (active && d) setData(d)
			})
			.catch(() => {
				// Optional UI — swallow errors and render nothing.
			})
		return () => {
			active = false
		}
	}, [])

	const trending = data?.trending ?? null
	// Hide the all-time pill when it's the same state as trending (redundant).
	const allTime = data?.allTime && data.allTime.code !== trending?.code ? data.allTime : null

	const hasHotState = Boolean(trending || allTime)

	return (
		<AnimatePresence initial={false}>
			{hasHotState && (
				<motion.div
					// Animate height so the content below eases down instead of snapping.
					// `overflow-hidden` keeps the chips clipped while the box expands.
					className="overflow-hidden"
					initial={
						prefersReducedMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0, y: -4 }
					}
					animate={{ opacity: 1, height: 'auto', y: 0 }}
					exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, y: -4 }}
					transition={
						prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }
					}
				>
					<span className="mb-1.5 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-text-muted">
						<Flame className="size-3 text-brand" aria-hidden />
						Hot destinations
					</span>
					<div className="flex flex-wrap items-center gap-1.5">
						{trending && (
							<span
								className="hot-pulse inline-flex items-center gap-1.5 rounded-md border border-brand/50 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-text-primary"
								title={`Trending — the most-routed destination over the last 30 days (${trending.count} routes)`}
							>
								<Flame className="size-3.5 text-brand" aria-hidden />
								{trending.name}
							</span>
						)}
						{allTime && (
							<span
								className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary"
								title={`All-time most-routed destination (${allTime.count} routes)`}
							>
								<Trophy className="size-3.5 text-text-muted" aria-hidden />
								{allTime.name}
							</span>
						)}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
